import { makeAutoObservable } from 'mobx';
import { type ICategoriesStore } from './ICategoriesStore';
import { Category } from 'domains/category';
import { ICategoriesService } from 'application/services/categories/ICategoriesService';
import { CategoryRequestDTO } from 'infrastructure/dtos/category';

export class CategoriesStore implements ICategoriesStore {
  private _categories: Category[] | undefined = undefined;
  private _originalCategories: Category[] | undefined = undefined;
  private _isLoading: boolean = false;

  constructor(private readonly service: ICategoriesService) {
    makeAutoObservable(this);
  }

  set isLoading(value: boolean) {
    this._isLoading = value;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set categories(value: Category[] | undefined) {
    this._categories = value;
  }

  get categories(): Category[] | undefined {
    return this._categories;
  }

  set originalCategories(value: Category[] | undefined) {
    this._originalCategories = value;
  }

  get originalCategories(): Category[] | undefined {
    return this._originalCategories;
  }

  private findParentCategory(parentId: string): Category | undefined {
    if (!this._categories) {
      return undefined;
    }

    return this._categories.find(c => c.id === parentId);
  }

  public async fetchCategories(): Promise<void> {
    if (this._categories) {
      return;
    }

    this.isLoading = true;

    const categories = await this.service.getCategories();

    this.categories = categories;
    this.originalCategories = categories;

    this.isLoading = false;
  }

  public async createMainCategory(dto: CategoryRequestDTO): Promise<void> {
    if (!this._categories) {
      return;
    }

    const newCategoryId = Date.now().toString();

    const newCategory = new Category(newCategoryId, dto.name, []);

    const updatedCategories = [newCategory, ...(this._categories || [])];

    this.categories = updatedCategories;
    this.originalCategories = updatedCategories;
  }

  public async createSubCategory(dto: CategoryRequestDTO): Promise<void> {
    if (!this._categories) {
      return;
    }

    this.isLoading = true;

    const newCategory = await this.service.createCategory(dto);

    this.isLoading = false;

    const parentCategory = this.findParentCategory(dto.parent_id);

    if (!parentCategory) {
      return;
    }

    const updatedParentCategory = {
      ...parentCategory,
      children: [newCategory, ...(parentCategory.children || [])],
    };

    const updatedCategories = this._categories.map(c =>
      c.id === parentCategory.id ? updatedParentCategory : c
    );

    this.categories = updatedCategories;
    this.originalCategories = updatedCategories;
  }

  public async updateCategory(category: Category): Promise<void> {
    if (!this._categories) {
      return;
    }

    const updatedCategories = this._categories.map(c => (c.id === category.id ? category : c));

    this.categories = updatedCategories;
    this.originalCategories = updatedCategories;
  }

  public searchCategories(query: string): void {
    if (!this._originalCategories) {
      return;
    }

    if (query.length === 0) {
      this.categories = this._originalCategories;
      return;
    }

    const searchQuery = query.toLowerCase();
    const matchedParentIds = new Set<string>();

    // First pass: Search through children and collect matching parent IDs
    const categoriesWithMatchedChildren = this._originalCategories.map(category => {
      const matchedChildren =
        category.children?.filter(child => child.name.toLowerCase().includes(searchQuery)) || [];

      if (matchedChildren.length > 0) {
        matchedParentIds.add(category.id);
      }

      return {
        ...category,
        children: matchedChildren,
      };
    });

    // Second pass: Filter categories based on both criteria
    const filteredCategories = categoriesWithMatchedChildren.filter(category => {
      // Include if it has matching children (parent was already included)
      if (matchedParentIds.has(category.id)) {
        return true;
      }

      // Check parent name only if it wasn't included by children
      return category.name.toLowerCase().includes(searchQuery);
    });

    this.categories = filteredCategories;
  }
}
